import * as THREE from 'three'
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader'
import {EffectComposer} from 'three/examples/jsm/postprocessing/EffectComposer'
import {RenderPass} from 'three/examples/jsm/postprocessing/RenderPass'
import {UnrealBloomPass} from 'three/examples/jsm/postprocessing/UnrealBloomPass'

const TWEEN = require('@tweenjs/tween.js')
import {MeshSurfaceSampler} from "three/examples/jsm/math/MeshSurfaceSampler";
import {LightGenerator} from "./LightGenerator";
import {getSize, scrollSpeed, createFloatingTextTexture} from "./Utils";

/**
 * Debug
 */
// const stats = new Stats()
// stats.showPanel(0) // 0: fps, 1: ms, 2: mb, 3+: custom
// document.body.appendChild(stats.dom)

const canvas = document.querySelector('canvas.webgl')
const progressContainer = document.getElementById("progress");
const progressBar = document.getElementById("loading");
const checkbox = document.getElementById('dayNightSwitcher');
const spans = document.getElementById("menuToggle").getElementsByTagName('span');
const loadingScreen = document.getElementById("loadingscreen")
const title = document.getElementById("title")
const startButton = document.getElementById("start-button");
const chaosFill = document.getElementById("chaos-fill");
const chaosText = document.getElementById("chaos-text");
const logoEl = document.getElementById('logo');
const spinHint = document.getElementById('spin-hint');
let started = false;
let introPlaying = false;
const popupPoints = [0, 1.65, 2.13, -3.10, -2.0]
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}
const cursor = {
    x: 0,
    y: 0
}

const scene = new THREE.Scene()
scene.background = new THREE.Color(0x050510);

const lightGenerator = new LightGenerator(scene)
const loadingManager = new THREE.LoadingManager();
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)


loadingManager.onProgress = function (url, loaded, total) {
    progressBar.innerText = 'Loading... ' + Math.floor((loaded / total) * 100) + "%"
}

loadingManager.onLoad = function (url, loaded, total) {
    progressContainer.style.display = "none";
    loadingScreen.classList.add("transparentloading")
    startButton.style.display = "block";
    title.style.display = "none"
}


// Renderer
const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setClearColor(0x050510, 1);

renderer.outputEncoding = THREE.sRGBEncoding;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.VSMShadowMap;
renderer.toneMapping = THREE.LinearToneMapping;
renderer.toneMappingExposure = 1.0;

// Postprocessing - Bloom for neon glow
const renderScene = new RenderPass(scene, null); // camera set later
const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(sizes.width, sizes.height),
    0.03,  // strength - barely visible bloom for daylight
    0.1,   // radius
    0.95   // threshold - only extremely bright things glow
);
const composer = new EffectComposer(renderer);
composer.addPass(renderScene);
composer.addPass(bloomPass);

// Camera
const camera = new THREE.PerspectiveCamera(64, sizes.width / sizes.height, 1, 90);
camera.position.set(0, 90, 0);
scene.add(camera);
renderScene.camera = camera;

// Controls
const controls = new OrbitControls(camera, canvas);
controls.target.set(0, 0, 0);
controls.enablePan = false;
controls.minPolarAngle = Math.PI / 2.4;
controls.maxPolarAngle = Math.PI / 2.15;
controls.minDistance = 8;
controls.maxDistance = 20;
controls.enableDamping = false;
controls.rotateSpeed = 0.25;


// Lights - Night palette
const hemiLight = lightGenerator.createHemiLight()
const sunLight = lightGenerator.createSunlight()
const ambientLight = new THREE.AmbientLight(0x5555aa, 0.6);
scene.add(ambientLight);

// Bitcoin Moon in the sky
const sunGroup = new THREE.Group();
sunGroup.position.set(25, 4, -25);
scene.add(sunGroup);

// Starfield
function createStarfield() {
    const starCount = 4000;
    const starGeo = new THREE.BufferGeometry();
    const starPositions = new Float32Array(starCount * 3);
    const starColors = new Float32Array(starCount * 3);
    const color = new THREE.Color();
    for (let i = 0; i < starCount; i++) {
        const r = 60 + Math.random() * 40;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        starPositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        starPositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        starPositions[i * 3 + 2] = r * Math.cos(phi);
        const starType = Math.random();
        if (starType < 0.8) {
            color.setHSL(0.6, 0.1, 0.8 + Math.random() * 0.2);
        } else if (starType < 0.9) {
            color.setHSL(0.0, 0.3, 0.8 + Math.random() * 0.2);
        } else {
            color.setHSL(0.15, 0.3, 0.8 + Math.random() * 0.2);
        }
        starColors[i * 3] = color.r;
        starColors[i * 3 + 1] = color.g;
        starColors[i * 3 + 2] = color.b;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
    const starMat = new THREE.PointsMaterial({
        size: 0.15,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        sizeAttenuation: true
    });
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);
    return stars;
}
const starfield = createStarfield();

// Load 3D Bitcoin model
gltfLoader.load('/models/sakura/bitcoin.glb', function (gltf) {
    const bitcoin = gltf.scene;
    bitcoinModel = bitcoin;
    // Auto-scale to fit moon size
    const size = getSize(bitcoin);
    const targetSize = 5;
    const autoScale = targetSize / Math.max(size.x, size.y, size.z);
    bitcoin.scale.setScalar(autoScale);
    console.log('Bitcoin model size:', size, 'scale:', autoScale);
    
    bitcoin.traverse(node => {
        if (node.isMesh) {
            // Keep original texture, only slight glow so it stays visible at night
            makeNeonMaterial(node, 0xffaa00, 0.08);
        }
    });
    
    sunGroup.add(bitcoin);
});
// Daylight mode - no neon lights
let sakuraLight = null;
let characterRimLight = null;
let houseLight = null;
let pondLight = null;
let catLight = null;
const spotLight = lightGenerator.createSakuraSpotlight();
spotLight.visible = false;

// Accent lights disabled for daylight
// lightGenerator.createNeonLight(...)

startButton.onclick = function () {
    loadingScreen.classList.remove("transparentloading")
    loadingScreen.classList.add("hidden");
    controls.enabled = false;
    introPlaying = true;

    new TWEEN.Tween(camera.position)
        .to({x: 0, y: 3.5, z: 18}, 3000)
        .easing(TWEEN.Easing.Cubic.Out)
        .onComplete(() => {
            introPlaying = false;
            controls.enabled = true;
        })
        .start();
    started = true;
}


// Helper: apply neon emissive tint to meshes
function makeNeonMaterial(mesh, color, intensity = 0.3) {
    if (!mesh.material) return;
    if (Array.isArray(mesh.material)) {
        mesh.material.forEach(m => {
            if (m.emissive !== undefined) {
                m.emissive = new THREE.Color(color);
                m.emissiveIntensity = intensity;
            }
        });
    } else {
        if (mesh.material.emissive !== undefined) {
            mesh.material.emissive = new THREE.Color(color);
            mesh.material.emissiveIntensity = intensity;
        }
    }
}

var island;
var clouds;
let animations = []
gltfLoader.load(
    '/models/sakura/island2.glb',
    function (gltf) {
        island = gltf.scene;
        clouds = island.getObjectByName('Clouds');
        gltf.scene.traverse(function (node) {
            if (node.isMesh) {
                node.castShadow = true;
                node.receiveShadow = true;
                // Keep island natural, no heavy emissive
                // Warm sunlight reflection on island
                makeNeonMaterial(node, 0xffeedd, 0);
            }
        });
        scene.add(island)
    });


gltfLoader.load(
    '/models/treeline.glb',
    function (gltf) {
        var surface = gltf.scene.children[0]

        var sampler = new MeshSurfaceSampler(surface).build();
        const tempPosition = new THREE.Vector3();
        const tempObject = new THREE.Object3D();
        let yc = 0

        gltfLoader.load(
            '/models/flower.glb',
            function (gltf) {

                var blossom = gltf.scene.getObjectByName('Blossom');
                var stem = gltf.scene.getObjectByName('Stem');

                let blossomMaterial = new THREE.MeshLambertMaterial({
                    emissive: new THREE.Color(0xff00ff).convertSRGBToLinear(),
                    emissiveIntensity: 0.12,
                });
                const color = new THREE.Color();
                const blossomPalette = [0xff00ff, 0x00ffff, 0x39ff14];

                for (let i = 0; i < 1000; i++) {

                    sampler.sample(tempPosition);
                    if (Math.abs(tempPosition.x) + Math.abs(tempPosition.z) < 5.0) continue
                    let scale = Math.random() * 0.03 + 0.02
                    let ySize = 0
                    tempObject.position.set(tempPosition.x, tempPosition.y - 0.03, tempPosition.z);
                    tempObject.scale.setScalar(scale);
                    tempObject.updateMatrix();

                    color.setHex(blossomPalette[Math.floor(Math.random() * blossomPalette.length)]);

                    var instancedBlossom = new THREE.InstancedMesh(blossom.geometry, blossomMaterial, 1);
                    var instancedStem = new THREE.InstancedMesh(stem.geometry, stem.material, 1);

                    instancedBlossom.setMatrixAt(0, tempObject.matrix);
                    instancedBlossom.position.y = instancedBlossom.position.y - 0.03;
                    instancedStem.setMatrixAt(0, tempObject.matrix);
                    instancedBlossom.setColorAt(0, color.convertSRGBToLinear());

                    instancedBlossom.castShadow = true;
                    instancedStem.castShadow = true;
                    instancedBlossom.receiveShadow = true;
                    scene.add(instancedBlossom);
                    scene.add(instancedStem);

                }
            });
        gltfLoader.load(
            '/models/sakura/Sakura.glb',
            function (gltf) {

                var tree = gltf.scene.getObjectByName('Tree001').children[1];
                var stem = gltf.scene.getObjectByName('Tree001').children[0];
                let treeMaterial = new THREE.MeshLambertMaterial();

                const color = new THREE.Color();
                const treePalette = [0xff00ff, 0xbf00ff, 0x00ffff];

                for (let i = 0; i < 60; i++) {
                    sampler.sample(tempPosition);
                    if (Math.abs(tempPosition.x) + Math.abs(tempPosition.z) < 5.0) continue
                    let scale = Math.random() * .05 + .04;

                    tempObject.position.set(tempPosition.x, tempPosition.y + 0.2, tempPosition.z);

                    tempObject.scale.setScalar(scale);
                    tempObject.updateMatrix();

                    color.setHex(treePalette[Math.floor(Math.random() * treePalette.length)]);

                    var instancedTree = new THREE.InstancedMesh(tree.geometry, treeMaterial, 1);
                    let ySize = 0
                    let ySizeStem = 0
                    instancedTree.setMatrixAt(0, tempObject.matrix);
                    instancedTree.position.y = instancedTree.position.y + 0.2;
                    instancedTree.setColorAt(0, color.convertSRGBToLinear());
                    var instancedStem = new THREE.InstancedMesh(stem.geometry, stem.material, 1);
                    instancedStem.setMatrixAt(0, tempObject.matrix)

                    instancedStem.position.y = instancedStem.position.y - ySizeStem;

                    instancedTree.castShadow = true;
                    instancedTree.receiveShadow = true;
                    scene.add(instancedTree);
                    scene.add(instancedStem);

                }
            });
        gltfLoader.load(
            '/models/sakura/sonic_the_hedgehog_palm_tree.glb',
            function (gltf) {
                for (let i = 0; i < 40; i++) {
                    sampler.sample(tempPosition);
                    if (Math.abs(tempPosition.x) + Math.abs(tempPosition.z) < 5.0) continue
                    let tree = gltf.scene.clone()
                    let scale = (Math.random() * .06 + .05) / 4;
                    tree.scale.set(scale, scale, scale);
                    tree.rotation.y = Math.random() * Math.PI
                    tree.position.set(tempPosition.x, tempPosition.y, tempPosition.z);
                    tree.traverse(node => {
                        if (node.isMesh) {
                            makeNeonMaterial(node, 0xccffcc, 0);
                        }
                    });
                    scene.add(tree);
                }
            });

        gltfLoader.load(
            '/models/sakura/lowpoly_flower_bushes.glb',
            function (gltf) {
                for (let i = 0; i < 30; i++) {
                    sampler.sample(tempPosition);
                    if (Math.abs(tempPosition.x) + Math.abs(tempPosition.z) < 5.0) continue
                    let bush = gltf.scene.clone()
                    let scale = Math.random() * .04 + .03;
                    bush.scale.set(scale, scale, scale);
                    bush.rotation.y = Math.random() * Math.PI
                    bush.position.set(tempPosition.x, tempPosition.y, tempPosition.z);
                    bush.traverse(node => {
                        if (node.isMesh) {
                            makeNeonMaterial(node, 0xccffcc, 0);
                        }
                    });
                    scene.add(bush);
                }
            });

        gltfLoader.load(
            '/models/sakura/lowpoly_flower_bushes_1.glb',
            function (gltf) {
                for (let i = 0; i < 30; i++) {
                    sampler.sample(tempPosition);
                    if (Math.abs(tempPosition.x) + Math.abs(tempPosition.z) < 5.0) continue
                    let bush = gltf.scene.clone()
                    let scale = Math.random() * .04 + .03;
                    bush.scale.set(scale, scale, scale);
                    bush.rotation.y = Math.random() * Math.PI
                    bush.position.set(tempPosition.x, tempPosition.y, tempPosition.z);
                    bush.traverse(node => {
                        if (node.isMesh) {
                            makeNeonMaterial(node, 0xccffcc, 0);
                        }
                    });
                    scene.add(bush);
                }
            });
    });





var bridge
loadObject(bridge, 'bridge2.glb', 0.45, 9.8, -0.2, -6, 2.2, false)
var pond
loadObject(pond, 'spring_pond.glb', 0.2, 9.5, 0, -1, 0, false);
var japanGates
loadObject(japanGates, 'JapanGates.glb', 1, 0, 0, 0, 0, false);
function getLowestFootY(root) {
    const box = new THREE.Box3();
    const tempBox = new THREE.Box3();
    root.updateMatrixWorld(true);
    let found = false;
    root.traverse((node) => {
        if (node.isMesh && node.geometry && /leg|foot|toe/i.test(node.name)) {
            if (!node.geometry.boundingBox) {
                node.geometry.computeBoundingBox();
            }
            tempBox.copy(node.geometry.boundingBox).applyMatrix4(node.matrixWorld);
            if (!found || tempBox.min.y < box.min.y) {
                box.copy(tempBox);
                found = true;
            }
        }
    });
    return found ? box.min.y : null;
}

function getMeshBoundingBox(root) {
    const box = new THREE.Box3();
    const tempBox = new THREE.Box3();
    root.updateMatrixWorld(true);
    root.traverse((node) => {
        if (node.isMesh && node.geometry) {
            if (!node.geometry.boundingBox) {
                node.geometry.computeBoundingBox();
            }
            tempBox.copy(node.geometry.boundingBox).applyMatrix4(node.matrixWorld);
            box.union(tempBox);
        }
    });
    return box;
}

function ensureVisible(mesh) {
    if (!mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
        // Remove fake self-illumination so real lights control the look
        if (m.emissive !== undefined) {
            m.emissive = new THREE.Color(0x000000);
            m.emissiveIntensity = 0;
        }
        // Only fix truly black materials
        if (m.color !== undefined) {
            const lum = m.color.r * 0.299 + m.color.g * 0.587 + m.color.b * 0.114;
            if (lum < 0.05) {
                m.color = new THREE.Color(0xaaaaaa);
            }
        }
        m.needsUpdate = true;
    });
}

var house;
gltfLoader.load('/models/sakura/skibidi_toilet.glb', function (gltf) {
    house = gltf.scene;
    const size = getSize(house);
    const targetHeight = 2.5;
    const autoScale = targetHeight / size.y;
    house.scale.setScalar(autoScale);
    console.log('Skibidi size:', size, 'scale:', autoScale);
    const meshBox = getMeshBoundingBox(house);
    console.log('Skibidi meshBox:', meshBox.min, meshBox.max);
    house.traverse(function (node) {
        if (node.isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
            ensureVisible(node);
            if (node.material) {
                const mats = Array.isArray(node.material) ? node.material : [node.material];
                mats.forEach(m => {
                    if (m.metalness !== undefined) m.metalness = 0;
                    if (m.roughness !== undefined) m.roughness = 1;
                });
            }
        }
    });
    house.position.set(-6, -meshBox.min.y + 0.5, -6);
    house.rotation.y = Math.PI;
    house.userData.basePosition = house.position.clone();
    house.userData.baseRotation = house.rotation.clone();
    scene.add(house);
    console.log('Skibidi loaded at', house.position);
});

var house2;
gltfLoader.load('/models/sakura/obama.glb', function (gltf) {
    house2 = gltf.scene;
    const size = getSize(house2);
    const targetHeight = 2.5;
    const autoScale = targetHeight / size.y;
    house2.scale.setScalar(autoScale);
    console.log('Obama size:', size, 'scale:', autoScale);
    const meshBox = getMeshBoundingBox(house2);
    console.log('Obama meshBox:', meshBox.min, meshBox.max);
    house2.traverse(function (node) {
        if (node.isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
            ensureVisible(node);
            if (node.material) {
                const mats = Array.isArray(node.material) ? node.material : [node.material];
                mats.forEach(m => {
                    if (m.metalness !== undefined) m.metalness = 0;
                    if (m.roughness !== undefined) m.roughness = 1;
                });
            }
        }
    });
    house2.position.set(6, -meshBox.min.y + 0.5, 6);
    house2.userData.basePosition = house2.position.clone();
    house2.userData.baseRotation = house2.rotation.clone();
    scene.add(house2);
    console.log('Obama loaded at', house2.position);
});
var stones
loadObject(stones, 'stones.glb', 1, 0, 0, 0, 0, false);
var haggord;
gltfLoader.load('/models/sakura/haggord.glb', function (gltf) {
    haggord = gltf.scene;
    const size = getSize(haggord);
    const targetHeight = 1.2;
    const autoScale = targetHeight / size.y;
    haggord.scale.setScalar(autoScale);
    console.log('Haggord size:', size, 'scale:', autoScale);
    const meshBox = getMeshBoundingBox(haggord);
    console.log('Haggord meshBox:', meshBox.min, meshBox.max);

    haggord.traverse(function (node) {
        if (node.isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
            ensureVisible(node);
        }
    });

    haggord.position.set(3, -meshBox.min.y + 0.5, 3);
    scene.add(haggord);
    console.log('Haggord loaded at', haggord.position);

    // Setup animations
    if (gltf.animations && gltf.animations.length > 0) {
        var mixer = new THREE.AnimationMixer(haggord);
        animations.push(mixer);

        gltf.animations.forEach(clip => {
            console.log('Found haggord animation:', clip.name);
        });

        const firstClip = gltf.animations[0];
        const action = mixer.clipAction(firstClip);
        action.loop = THREE.LoopRepeat;
        action.play();
    }
});
var lantern, lanternLights = []
loadObject(lantern, 'lantern.glb', 0.1, 0, 0, -9.5, 0, true, lanternLights);
var lantern2, lanternLights2 = []
loadObject(lantern2, 'lantern.glb', 0.1, 9.5, 0, -2, 0, true, lanternLights2);
var lantern3, lanternLights3 = []
loadObject(lantern2, 'lantern.glb', 0.1, -3.5, 0, 10, 0, true, lanternLights3);

var sakura;
var bitcoinModel;
var actionIdle, actionJog, actionRun, activeAction;
let rootBone = null;
var currentAnimSpeed = 0;
gltfLoader.load(
    '/models/sakura/sonicfanfilm_anim.glb', function (gltf) {
        sakura = gltf.scene;
        
        // Auto-scale to match old character visual size (~1.5 units height)
        const size = getSize(sakura);
        const targetHeight = 2.2;
        const autoScale = targetHeight / size.y;
        sakura.scale.setScalar(autoScale);
        console.log('Sonic fanfilm model raw size:', size, 'applied scale:', autoScale);
        
        // Position model so feet touch ground properly
        const footY = getLowestFootY(sakura);
        if (footY !== null) {
            sakura.position.y = -footY;
            console.log('Sonic footY:', footY, 'position.y:', sakura.position.y);
        } else {
            const worldBox = new THREE.Box3().setFromObject(sakura);
            sakura.position.y = -worldBox.min.y;
            console.log('Sonic worldBox.min.y (fallback):', worldBox.min.y, 'position.y:', sakura.position.y);
        }
        sakura.userData.baseY = sakura.position.y;
        
        // Setup animations
        if (gltf.animations && gltf.animations.length > 0) {
            var mixer = new THREE.AnimationMixer(sakura);
            animations.push(mixer)
            
            gltf.animations.forEach(clip => {
                console.log('Found animation:', clip.name);
            });
            
            const idleClip = gltf.animations.find(a => a.name === 'Idle_NEW');
            const jogClip = gltf.animations.find(a => a.name === 'NEW JOG');
            const runClip = gltf.animations.find(a => a.name === 'Run1');
            
            if (idleClip) {
                actionIdle = mixer.clipAction(idleClip);
                actionIdle.loop = THREE.LoopRepeat;
            }
            if (jogClip) {
                actionJog = mixer.clipAction(jogClip);
                actionJog.loop = THREE.LoopRepeat;
            }
            if (runClip) {
                actionRun = mixer.clipAction(runClip);
                actionRun.loop = THREE.LoopRepeat;
            }
            
            // Default to idle
            if (actionIdle) {
                actionIdle.play();
                activeAction = actionIdle;
            }
            
            console.log('[SONIC ANIM] idle:', !!actionIdle, 'jog:', !!actionJog, 'run:', !!actionRun);
            console.log('[SONIC ANIM] activeAction set to:', activeAction ? activeAction.getClip().name : 'NONE');
            [actionIdle, actionJog, actionRun].forEach((act, idx) => {
                if (act) console.log(`[SONIC ANIM] ${act.getClip().name} weight=${act.getEffectiveWeight().toFixed(2)} running=${act.isRunning()}`);
            });
        }
        
        traverse(gltf)
        
        // Find root bone to cancel unwanted tilt from animations
        sakura.traverse(node => {
            if (node.isBone && (!node.parent || !node.parent.isBone)) {
                rootBone = node;
                console.log('Root bone found:', rootBone.name);
            }
        });
        
        // Clean materials — let real lights handle shading, preserve original colours
        sakura.traverse(function (node) {
            if (node.isMesh && node.material) {
                const mats = Array.isArray(node.material) ? node.material : [node.material];
                mats.forEach(m => {
                    // Remove fake self-illumination
                    if (m.emissive !== undefined) {
                        m.emissive = new THREE.Color(0x000000);
                        m.emissiveIntensity = 0;
                    }
                    // Force matte look so metalness doesn't blow out under lights
                    if (m.metalness !== undefined) m.metalness = 0;
                    if (m.roughness !== undefined) m.roughness = 1;
                    m.needsUpdate = true;
                });
            }
        });
        
        sakura.visible = true
        scene.add(sakura);
        
        createTrailSystem();
    });


function traverse(gltf) {
    gltf.scene.traverse(function (node) {
        if (node.isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
        }
    });
}


function loadObject(refObj, name, scale, x, y, z, rotation, withLight, lights) {
    gltfLoader.load(
        '/models/sakura/' + name, function (gltf) {
            refObj = gltf.scene;
            gltf.scene.traverse(function (node) {
                if (node.isMesh) {
                    node.castShadow = true;
                    node.receiveShadow = true;
                    // Small objects get subtle neon accent, not full glow
                    if (scale < 0.3) {
                        const neonColors = [0xff00ff, 0x00ffff, 0x39ff14, 0xffff00, 0xff5500];
                        const randColor = neonColors[Math.floor(Math.random() * neonColors.length)];
                        makeNeonMaterial(node, randColor, 0);
                    }
                }
            });
            scene.add(refObj);
            refObj.scale.set(scale, scale, scale);
            if (rotation) {
                refObj.rotation.y = rotation
            }
            refObj.position.set(x, y, z);
            if (withLight) {
                let light = new THREE.PointLight(0xff00ff, 2, 8, 2);
                let height = 1.2
                light.position.set(refObj.position.x, refObj.position.y + height, refObj.position.z);
                scene.add(light);
                const fl1 = new THREE.PointLight(0x00ffff, 3, 3, 2);
                fl1.position.set(refObj.position.x + 0.5, refObj.position.y + height, refObj.position.z);
                scene.add(fl1);
                const fl2 = new THREE.PointLight(0xffff00, 3, 3, 2);
                fl2.position.set(refObj.position.x - 1, refObj.position.y + height, refObj.position.z);
                scene.add(fl2);
                lights.length = 0
                lights.push(light, fl1, fl2)
                lights.forEach(l => l.visible = false)
            }
            switchDayNight()

        })
}


// ========== MEME OBJECTS ==========

// Floating Sonic Rings
const rings = [];
function createRings() {
    const ringGeo = new THREE.TorusGeometry(0.45, 0.09, 8, 24);
    for (let i = 0; i < 20; i++) {
        const ringMat = new THREE.MeshBasicMaterial({ 
            color: 0xffff00,
            transparent: true,
            opacity: 0.9
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        const angle = (i / 20) * Math.PI * 2;
        ring.position.set(Math.sin(angle) * 12, 1.5 + Math.sin(i * 3) * 0.5, Math.cos(angle) * 12);
        ring.userData = { baseY: ring.position.y, angle: angle, speed: 1 + Math.random() };
        scene.add(ring);
        rings.push(ring);
    }
}
createRings();

// Floating Meme Text Sprites
const memeTexts = [];
function createMemeTexts() {
    const texts = ["NO EXIT", "STILL EARLY", "TOO LATE", "SPIN", "WHY", "AGAIN", "FOMO", "VOID"];
    const colors = ["#ff00ff", "#00ffff", "#39ff14", "#ffff00"];
    for (let i = 0; i < 8; i++) {
        const tex = createFloatingTextTexture(texts[i], colors[i % colors.length]);
        const spriteMat = new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.95 });
        const sprite = new THREE.Sprite(spriteMat);
        const angle = (i / 8) * Math.PI * 2 + 0.5;
        sprite.position.set(Math.sin(angle) * 12, 3.5 + Math.random() * 2, Math.cos(angle) * 12);
        sprite.scale.set(2.5, 0.6, 1);
        sprite.userData = { baseY: sprite.position.y, phase: Math.random() * Math.PI * 2 };
        scene.add(sprite);
        memeTexts.push(sprite);
    }
}
createMemeTexts();

// Lightning Bolts
const bolts = [];
function createLightningBolts() {
    const boltGeo = new THREE.ConeGeometry(0.08, 2.5, 4);
    for (let i = 0; i < 6; i++) {
        const boltMat = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.7 });
        const bolt = new THREE.Mesh(boltGeo, boltMat);
        const angle = (i / 6) * Math.PI * 2;
        bolt.position.set(Math.sin(angle) * 8, 4.5, Math.cos(angle) * 8);
        bolt.rotation.z = Math.PI;
        bolt.userData = { active: false, timer: Math.random() * 100 };
        scene.add(bolt);
        bolts.push(bolt);
    }
}
createLightningBolts();

// Character Afterimage Trail
let trailMeshes = [];
const TRAIL_COUNT = 6;
function createTrailSystem() {
    if (!sakura) return;
    for (let i = 0; i < TRAIL_COUNT; i++) {
        const trailMesh = sakura.clone();
        trailMesh.traverse(node => {
            if (node.isMesh && node.material) {
                if (Array.isArray(node.material)) {
                    node.material = node.material.map(m => {
                        const clone = m.clone();
                        clone.transparent = true;
                        clone.opacity = 0.25 * (1 - i / TRAIL_COUNT);
                        clone.emissive = new THREE.Color(0x00ffff);
                        clone.emissiveIntensity = 0.4 * (1 - i / TRAIL_COUNT);
                        return clone;
                    });
                } else {
                    node.material = node.material.clone();
                    node.material.transparent = true;
                    node.material.opacity = 0.25 * (1 - i / TRAIL_COUNT);
                    node.material.emissive = new THREE.Color(0x00ffff);
                    node.material.emissiveIntensity = 0.4 * (1 - i / TRAIL_COUNT);
                }
            }
        });
        trailMesh.visible = false;
        scene.add(trailMesh);
        trailMeshes.push(trailMesh);
    }
}

// Chaos Meter Logic
let chaosLevel = 0;
function updateChaosMeter(delta) {
    let speed = Math.abs(delta) * 100;
    chaosLevel = Math.min(100, chaosLevel + speed * 2);
    chaosLevel = Math.max(0, chaosLevel - 0.4);
    
    if (chaosFill) chaosFill.style.width = chaosLevel + "%";
    
    let text = "IDLE";
    let color = "#00ffff";
    if (chaosLevel < 20) { text = "IDLE"; color = "#00ffff"; }
    else if (chaosLevel < 40) { text = "SLIGHTLY WRONG"; color = "#39ff14"; }
    else if (chaosLevel < 60) { text = "UNSTABLE"; color = "#ffff00"; }
    else if (chaosLevel < 80) { text = "BREAKING"; color = "#ff5500"; }
    else { text = "BEYOND CONTROL"; color = "#ff00ff"; }
    
    if (chaosText) {
        chaosText.innerText = text;
        chaosText.style.color = color;
        chaosText.style.textShadow = 'none';
    }
}

// Camera Shake
let shakeIntensity = 0;
function applyCameraShake() {
    if (shakeIntensity > 0 && camera) {
        camera.position.x += (Math.random() - 0.5) * shakeIntensity;
        camera.position.y += (Math.random() - 0.5) * shakeIntensity;
        camera.position.z += (Math.random() - 0.5) * shakeIntensity;
        shakeIntensity *= 0.85;
        if (shakeIntensity < 0.01) shakeIntensity = 0;
    }
}

// Floating HTML meme texts
function spawnFloatingMemeText() {
    const phrases = ["GO", "MORE", "FASTER", "AGAIN", "HIGHER", "SPIN", "NOW", "DONT STOP"];
    const el = document.createElement('div');
    el.className = 'floating-meme';
    el.innerText = phrases[Math.floor(Math.random() * phrases.length)];
    el.style.left = (20 + Math.random() * 60) + '%';
    el.style.top = (30 + Math.random() * 40) + '%';
    el.style.color = ['#ff00ff', '#00ffff', '#39ff14', '#ffff00'][Math.floor(Math.random() * 4)];
    document.body.appendChild(el);
    setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
    }, 3000);
}


window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
    composer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
})
window.addEventListener('mousemove', (event) => {
    cursor.x = event.clientX / sizes.width - 0.5
    cursor.y = -(event.clientY / sizes.height - 0.5)
})




//Interaction with Objects

//window.addEventListener('click', onDocumentMouseDown, false);

var raycaster = new THREE.Raycaster();
var mouse = new THREE.Vector2();

function onDocumentMouseDown(event) {
    // event.preventDefault();
    mouse.x = (event.clientX / renderer.domElement.clientWidth) * 2 - 1;
    mouse.y = -(event.clientY / renderer.domElement.clientHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    var intersects = raycaster.intersectObjects(scene.children);

    if (intersects.length > 0) {
        console.log(intersects)
        console.log(intersects[1].object.name);
        let obj = intersects[1]
        if (obj.object.name.indexOf('Object_4') > -1) {
            new TWEEN.Tween(camera.position)
                .to({x: obj.point.x, y: obj.point.y, z: obj.point.z + 5}, 1000)
                .easing(TWEEN.Easing.Cubic.Out)
                .start()
            ;
        }
    }
}


function switchDayNight() {
    // Always night mode - starfield sky
    canvas.style.background = '#050510';
    hemiLight.intensity = 0.55;
    sunLight.intensity = 0.9;
    sunLight.color.setHSL(0.6, 0.08, 0.88);
    document.body.style.color = "#fff";
    for (const span of spans) {
        span.style.background = "#fff";
        span.style.boxShadow = "none";
    }
    if (bloomPass) bloomPass.strength = 0.2;
    sunLight.visible = true;
    sunLight.castShadow = true;
}

var today = new Date();
var time = today.getHours();

// Always start in BRIGHT cartoon mode
checkbox.checked = true;
switchDayNight();

checkbox.addEventListener('change', (event) => {
    switchDayNight();
})


let azimuthalAngle;
let sakuraPos = 0;
let lastAzimuthalAngle = 0;
let i = 0;
let g = 0.8;
let lastForw = true;

const popups = document.getElementsByClassName("popup");
const clock = new THREE.Clock();

let bridgePoints = [1.90, 2.15, 2.40]
let housePoints = -0.20

let getScrollDelta = scrollSpeed(controls);
let memeSpawnTimer = 0;

// Smooth animation & rotation helpers
let smoothedMoveSpeed = 0;
let frameCount = 0;
let prevMoveSpeed = 0;
let prevActiveActionName = '';

function lerpAngle(a, b, t) {
    let diff = b - a;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return a + diff * t;
}



const tick = () => {
    controls.update()


    let prevAzimuthal = lastAzimuthalAngle;
    azimuthalAngle = controls.getAzimuthalAngle();
    lastAzimuthalAngle = azimuthalAngle;

    let prevPos = sakuraPos;
    sakuraPos = azimuthalAngle / (Math.PI * 2);
    if (sakuraPos < 0) {
        sakuraPos = 1.0 + sakuraPos;
    }

    // Direction based on raw angle delta with wrap-around fix
    let angleDelta = azimuthalAngle - prevAzimuthal;
    if (angleDelta > Math.PI) angleDelta -= Math.PI * 2;
    if (angleDelta < -Math.PI) angleDelta += Math.PI * 2;

    let forw = angleDelta > 0;
    if (Math.abs(angleDelta) < 0.001) {
        forw = lastForw;
    } else {
        lastForw = forw;
    }

    const moveSpeed = Math.abs(angleDelta) / (Math.PI * 2);
    if (sakura) {
        if (introPlaying) {
            // Keep Sonic in place, facing the camera during intro
            sakura.lookAt(camera.position);
            // Apply a 90-degree offset if needed so he faces correctly
            sakura.rotateY(Math.PI / 2);
        } else {
            sakura.position.x = Math.sin(azimuthalAngle) * 11.4;
            sakura.position.z = Math.cos(azimuthalAngle) * 11.4;
            const baseY = sakura.userData.baseY || 0;
            if (azimuthalAngle > bridgePoints[0] && azimuthalAngle <= bridgePoints[1]) {
                sakura.position.y = baseY + 0.3 + (azimuthalAngle - bridgePoints[0]) * 3
            } else if (azimuthalAngle > bridgePoints[1] && azimuthalAngle < bridgePoints[2]) {
                sakura.position.y = baseY + 0.3 + (bridgePoints[2] - azimuthalAngle) * 3
            } else {
                sakura.position.y = baseY;
            }
            // Target rotation: tangent to the circle in movement direction
            // Freeze facing direction when nearly stopped to avoid jitter
            if (moveSpeed >= 0.0003) {
                let targetRotation = forw
                    ? azimuthalAngle + Math.PI / 2
                    : azimuthalAngle - Math.PI / 2;
                sakura.rotation.y = lerpAngle(sakura.rotation.y, targetRotation, 0.25);
            }

            // Keep character upright (model may be tilted from lookAt in intro)
            sakura.rotation.set(0, sakura.rotation.y, 0);
            sakura.updateMatrix();

            // Update trail
            if (trailMeshes.length > 0) {
                for (let t = trailMeshes.length - 1; t > 0; t--) {
                    trailMeshes[t].position.copy(trailMeshes[t-1].position);
                    trailMeshes[t].rotation.copy(trailMeshes[t-1].rotation);
                    trailMeshes[t].visible = trailMeshes[t-1].visible && chaosLevel > 25;
                }
                trailMeshes[0].position.copy(sakura.position);
                trailMeshes[0].rotation.copy(sakura.rotation);
                trailMeshes[0].visible = true;
            }
            
            // Camera shake on fast movement
            if (moveSpeed > 0.005) {
                shakeIntensity = moveSpeed * 15;
            }
        }
    }



    for (let i = 0; i < popupPoints.length; i++) {
        if (started && popups[i]) {
            if (azimuthalAngle >= popupPoints[i] - 0.1 && azimuthalAngle < popupPoints[i] + 0.1) {
                popups[i].classList.remove("hidden");
                popups[i].classList.add("visible");
            } else {
                popups[i].classList.add("hidden");
                popups[i].classList.remove("visible");
            }
        }

    }

    if (clouds) {
        clouds.rotation.z -= 0.001;
    }
    
    let scrollDelta = getScrollDelta();

    // Derive target animation speed from real movement
    let rawSpeed = moveSpeed * 300; // normalized speed factor
    rawSpeed = Math.min(4, rawSpeed);

    // Hysteresis for idle: only go idle when nearly stopped
    if (moveSpeed < 0.0003) {
        rawSpeed = 0;
    }

    // Smooth speed changes so animations don't jitter between states
    if (moveSpeed < 0.0003) {
        // Fast linear decay when stopped
        smoothedMoveSpeed = Math.max(0, smoothedMoveSpeed - 0.25);
    } else {
        smoothedMoveSpeed += (rawSpeed - smoothedMoveSpeed) * 0.18;
    }

    // Switch animation based on smoothed speed: idle / jog / run
    if (actionIdle && actionJog && actionRun) {
        if (smoothedMoveSpeed < 0.05) {
            if (activeAction !== actionIdle) {
                if (activeAction) { activeAction.enabled = false; activeAction.weight = 0; }
                actionIdle.stop();
                actionIdle.enabled = true;
                actionIdle.weight = 1;
                actionIdle.play();
                activeAction = actionIdle;
            }
            actionIdle.timeScale = 1.0;
        } else if (smoothedMoveSpeed < 1.0) {
            if (activeAction !== actionJog) {
                if (activeAction) { activeAction.enabled = false; activeAction.weight = 0; }
                actionJog.stop();
                actionJog.enabled = true;
                actionJog.weight = 1;
                actionJog.play();
                activeAction = actionJog;
            }
            actionJog.timeScale = Math.max(0.6, Math.min(1.4, 0.6 + smoothedMoveSpeed * 0.8));
        } else {
            if (activeAction !== actionRun) {
                if (activeAction) { activeAction.enabled = false; activeAction.weight = 0; }
                actionRun.stop();
                actionRun.enabled = true;
                actionRun.weight = 1;
                actionRun.play();
                activeAction = actionRun;
            }
            actionRun.timeScale = Math.max(1.0, Math.min(2.0, 0.8 + smoothedMoveSpeed * 0.5));
        }
    }

    // Boost logo effect when running
    if (logoEl) {
        if (smoothedMoveSpeed > 0.8) {
            logoEl.classList.add('boost');
        } else {
            logoEl.classList.remove('boost');
        }
    }

    // Show spin hint after intro until user starts moving
    if (spinHint) {
        if (started && !introPlaying && smoothedMoveSpeed < 0.05) {
            spinHint.classList.remove('hidden');
        } else {
            spinHint.classList.add('hidden');
        }
    }

    // Quick weight blend for smoother visual transitions
    [actionIdle, actionJog, actionRun].forEach(action => {
        if (!action) return;
        const targetWeight = (action === activeAction) ? 1 : 0;
        action.weight += (targetWeight - action.weight) * 0.35;
        if (action.weight < 0.001) {
            action.weight = 0;
            action.enabled = false;
        } else if (action.weight > 0.999) {
            action.weight = 1;
        }
    });

    // Animation Mixer
    const delta = clock.getDelta();
    for (let animation of animations) {
        animation.update(delta)
    }

    // Cancel root bone tilt so character stays perpendicular to ground
    if (rootBone) {
        rootBone.rotation.x = 0;
        rootBone.rotation.z = 0;
    }
    
    // Debug logging
    frameCount++;
    const activeName = activeAction ? activeAction.getClip().name : 'NONE';
    
    // Log on animation switch
    if (activeName !== prevActiveActionName) {
        console.log(`[ANIM SWITCH] ${prevActiveActionName} -> ${activeName} | moveSpeed=${moveSpeed.toFixed(5)} smoothed=${smoothedMoveSpeed.toFixed(3)}`);
        [actionIdle, actionJog, actionRun].forEach(act => {
            if (act) console.log(`[ANIM SWITCH]   ${act.getClip().name}: weight=${act.weight.toFixed(3)} enabled=${act.enabled} running=${act.isRunning()}`);
        });
        prevActiveActionName = activeName;
    }
    
    // Log on stop event (mouse release)
    if (prevMoveSpeed >= 0.0003 && moveSpeed < 0.0003) {
        console.log(`[STOP EVENT] moveSpeed dropped to ${moveSpeed.toFixed(6)} | smoothed=${smoothedMoveSpeed.toFixed(3)} | active=${activeName}`);
        [actionIdle, actionJog, actionRun].forEach(act => {
            if (act) console.log(`[STOP EVENT]   ${act.getClip().name}: weight=${act.weight.toFixed(3)} enabled=${act.enabled} running=${act.isRunning()}`);
        });
    }
    
    // Periodic state log every 30 frames
    if (frameCount % 30 === 0) {
        console.log(`[ANIM STATE] frame=${frameCount} active=${activeName} moveSpeed=${moveSpeed.toFixed(5)} smoothed=${smoothedMoveSpeed.toFixed(3)}`);
        [actionIdle, actionJog, actionRun].forEach(act => {
            if (act) console.log(`[ANIM STATE]   ${act.getClip().name}: weight=${act.weight.toFixed(3)} enabled=${act.enabled} timeScale=${act.timeScale.toFixed(2)} running=${act.isRunning()}`);
        });
    }
    
    prevMoveSpeed = moveSpeed;
    
    updateChaosMeter(scrollDelta);
    applyCameraShake();
    
    // Animate rings
    rings.forEach((ring, idx) => {
        ring.rotation.y += 0.05;
        ring.rotation.x += 0.02;
        ring.position.y = ring.userData.baseY + Math.sin(performance.now() * 0.002 * ring.userData.speed + idx) * 0.3;
    });
    
    // Animate meme texts
    memeTexts.forEach((sprite, idx) => {
        sprite.position.y = sprite.userData.baseY + Math.sin(performance.now() * 0.001 + sprite.userData.phase) * 0.5;
        sprite.material.rotation += 0.001;
    });
    
    // Animate lightning bolts
    bolts.forEach(bolt => {
        bolt.userData.timer += 1;
        if (bolt.userData.timer > 100 + Math.random() * 60) {
            bolt.userData.active = !bolt.userData.active;
            bolt.userData.timer = 0;
            bolt.visible = bolt.userData.active;
            if (bolt.userData.active) {
                bolt.material.opacity = 0.6 + Math.random() * 0.4;
                bolt.scale.set(1 + Math.random() * 0.3, 1 + Math.random() * 0.5, 1 + Math.random() * 0.3);
            }
        }
    });
    
    // Animate Bitcoin Moon: orbit around island + self rotation
    if (sunGroup) {
        const orbitTime = performance.now() * 0.0002;
        const orbitRadius = 28;
        sunGroup.position.x = Math.sin(orbitTime) * orbitRadius;
        sunGroup.position.z = Math.cos(orbitTime) * orbitRadius;
        if (bitcoinModel) {
            bitcoinModel.rotation.y += 0.06;
        }
    }
    
    // Animate Obama (slow rotation)
    if (house2) {
        house2.rotation.y += 0.01;
    }
    
    // Animate Skibidi toilet (shake)
    if (house && house.userData.basePosition) {
        const t = performance.now() * 0.02;
        house.position.x = house.userData.basePosition.x + Math.sin(t) * 0.03;
        house.position.y = house.userData.basePosition.y + Math.cos(t * 1.3) * 0.02;
        house.position.z = house.userData.basePosition.z + Math.sin(t * 0.7) * 0.03;
        house.rotation.x = house.userData.baseRotation.x + Math.sin(t * 2) * 0.05;
        house.rotation.z = house.userData.baseRotation.z + Math.cos(t * 1.5) * 0.05;
    }
    
    // Spawn floating HTML meme texts randomly when chaos is high
    memeSpawnTimer += 1;
    if (chaosLevel > 50 && memeSpawnTimer > 60) {
        spawnFloatingMemeText();
        memeSpawnTimer = 0;
    }
    
    TWEEN.update();
    composer.render();
    window.requestAnimationFrame(tick)
}

tick()
